/**
 * Gingerlilly: Complete the Look.
 * Combines the current product (unless hidden) with its Search & Discovery complementary products
 * (fetched by cc-fetched-content), shows them as a 4-tile card, and lets the
 * shopper pick sizes and add the ticked products to the cart from a drawer.
 */
if (!customElements.get('gl-complete-the-look')) {
  const TILE_SLOTS = 4;

  const formatMoney = (cents, format) => {
    const fmt = (value, decimals, thousands = ',', decimal = '.') => {
      const [whole, frac] = (value / 100).toFixed(decimals).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(cents, 0);
        case 'amount_with_comma_separator': return fmt(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(cents, 0, '.');
        case 'amount_with_apostrophe_separator': return fmt(cents, 2, "'");
        default: return fmt(cents, 2);
      }
    });
  };

  class CompleteTheLookItem {
    constructor(el, parent) {
      this.el = el;
      this.parent = parent;
      this.data = JSON.parse(el.querySelector('.js-ctl-data').textContent);
      this.include = el.querySelector('.js-ctl-include');
      this.priceEl = el.querySelector('.js-ctl-price');
      this.errorEl = el.querySelector('.js-ctl-error');
      this.optionEls = [...el.querySelectorAll('.js-ctl-option')];
      this.oneSize = el.querySelector('.js-ctl-one-size');

      el.addEventListener('change', (evt) => {
        // Choosing a size includes the product.
        if (evt.target.matches('.js-ctl-value')) {
          this.include.checked = true;
          this.clearError();
        }
        if (evt.target === this.include && !this.include.checked) this.clearError();
        this.update();
        this.parent.update();
      });
      this.update();
    }

    get included() {
      return this.include.checked;
    }

    /** Selected option values, null where nothing is chosen yet. */
    get selectedOptions() {
      return this.optionEls.map((fs) => fs.querySelector('.js-ctl-value:checked')?.value ?? null);
    }

    get variant() {
      if (this.oneSize) return this.oneSize.checked ? this.data.variants[0] : null;
      if (this.optionEls.length === 0) return this.data.variants[0];
      const selected = this.selectedOptions;
      if (selected.includes(null)) return null;
      return this.data.variants.find((v) => v.options.every((o, i) => o === selected[i])) || null;
    }

    /** Price used for the total: chosen variant, otherwise the product's lowest price. */
    get price() {
      return this.variant ? this.variant.price : this.data.price;
    }

    selectOptions(options) {
      this.optionEls.forEach((fs, i) => {
        const input = [...fs.querySelectorAll('.js-ctl-value')].find((el) => el.value === options[i]);
        if (input && !input.disabled) input.checked = true;
      });
      if (this.variant) this.clearError();
      this.update();
    }

    /** Disable values that have no available variant given the other selections. */
    updateAvailability() {
      const selected = this.selectedOptions;
      this.optionEls.forEach((fs, i) => {
        fs.querySelectorAll('.js-ctl-value').forEach((input) => {
          const available = this.data.variants.some((v) => v.available
            && v.options[i] === input.value
            && v.options.every((o, j) => j === i || selected[j] === null || selected[j] === o));
          input.disabled = !available;
          if (!available && input.checked) input.checked = false;
        });
      });
    }

    update() {
      this.updateAvailability();
      const v = this.variant;
      const price = v ? v.price : this.data.price;
      const compareAt = v ? v.compare_at_price : this.data.compare_at_price;
      const fmt = this.parent.moneyFormat;
      this.priceEl.innerHTML = compareAt > price
        ? `<span class="gl-ctl-item__sale">${formatMoney(price, fmt)}</span> <s class="gl-ctl-item__was">${formatMoney(compareAt, fmt)}</s>`
        : formatMoney(price, fmt);
      this.el.classList.toggle('is-excluded', !this.included);
    }

    validate() {
      if (!this.included || this.variant) return true;
      const missing = this.optionEls.find((fs) => !fs.querySelector('.js-ctl-value:checked'));
      const name = missing ? missing.querySelector('legend').textContent.trim().toLowerCase() : 'size';
      this.errorEl.textContent = `Please select a ${name}`;
      this.errorEl.hidden = false;
      return false;
    }

    clearError() {
      if (this.errorEl) this.errorEl.hidden = true;
    }
  }

  class CompleteTheLook extends HTMLElement {
    connectedCallback() {
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.card = this.querySelector('.js-ctl-open');
      this.drawer = this.querySelector('.js-ctl-drawer');
      this.fetched = this.querySelector('cc-fetched-content');

      if (this.fetched.querySelector('.js-ctl-recs')) {
        this.build();
      } else {
        this.observer = new MutationObserver(() => {
          if (!this.fetched.querySelector('.js-ctl-recs')) return;
          this.observer.disconnect();
          this.build();
        });
        this.observer.observe(this.fetched, { childList: true });
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.variantChangeHandler) document.removeEventListener('on:variant:change', this.variantChangeHandler);
      // The drawer is moved to <body>, so remove it with the block (theme editor re-renders).
      if (this.drawer && this.drawer.parentElement === document.body) this.drawer.remove();
    }

    build() {
      const recs = this.fetched.querySelector('.js-ctl-recs');
      if (!recs || !recs.children.length) return;

      this.list = this.drawer.querySelector('.js-ctl-list');
      this.list.replaceChildren(this.querySelector('.js-ctl-current').content.cloneNode(true), ...recs.children);
      this.items = [...this.list.querySelectorAll('.gl-ctl-item')].map((el) => new CompleteTheLookItem(el, this));

      this.totalEl = this.drawer.querySelector('.js-ctl-total');
      this.shippingEl = this.drawer.querySelector('.js-ctl-shipping');
      this.addBtn = this.drawer.querySelector('.js-ctl-add');
      this.cartErrorEl = this.drawer.querySelector('.js-ctl-cart-error');

      this.renderCard();

      // Escape any sticky/transformed product column so the fixed drawer covers the viewport.
      document.body.appendChild(this.drawer);

      this.card.addEventListener('click', this.open.bind(this));
      this.addBtn.addEventListener('click', this.addToCart.bind(this));

      // Keep the current product's size in step with the main variant picker.
      this.variantChangeHandler = (evt) => {
        const variant = evt.detail && evt.detail.variant;
        const current = this.items.find((item) => item.el.hasAttribute('data-current'));
        if (variant && current && current.data.variants.some((v) => v.id === variant.id)) {
          current.selectOptions(variant.options);
          this.update();
        }
      };
      document.addEventListener('on:variant:change', this.variantChangeHandler);

      this.update();
    }

    renderCard() {
      const count = this.items.length;
      this.querySelector('.js-ctl-count').textContent = `${count} products`;

      // Up to four products: one tile each, spread across the row (2, 3 or 4 across).
      // Five or more: three images and a "+N" tile for the rest.
      const showMore = count > TILE_SLOTS;
      const imageTiles = showMore ? TILE_SLOTS - 1 : count;
      const tiles = this.items.slice(0, imageTiles).map((item) => {
        const tile = document.createElement('span');
        tile.className = 'gl-ctl-card__tile';
        const img = item.el.querySelector('.gl-ctl-item__img');
        if (img) tile.appendChild(img.cloneNode(true));
        return tile;
      });
      if (showMore) {
        const more = document.createElement('span');
        more.className = 'gl-ctl-card__tile gl-ctl-card__tile--more';
        more.textContent = `+${count - imageTiles}`;
        tiles.push(more);
      }
      const tilesEl = this.querySelector('.js-ctl-tiles');
      tilesEl.style.setProperty('--gl-ctl-cols', tiles.length);
      tilesEl.replaceChildren(...tiles);
      this.card.hidden = false;
    }

    open() {
      this.cartErrorEl.hidden = true;
      this.drawer.open(this.card);
      // Always open at the top of the list, whichever tile was clicked.
      this.list.scrollTop = 0;
    }

    get freeShippingThreshold() {
      const rate = Number(window.Shopify && Shopify.currency && Shopify.currency.rate) || 1;
      return Number(this.dataset.freeShippingThreshold) * rate;
    }

    update() {
      if (!this.items) return;
      const included = this.items.filter((item) => item.included);
      const total = included.reduce((sum, item) => sum + item.price, 0);

      this.totalEl.textContent = formatMoney(total, this.moneyFormat);
      if (this.shippingEl) {
        const threshold = this.freeShippingThreshold;
        this.shippingEl.hidden = !(threshold > 0 && total >= threshold);
      }

      // Every ticked product needs a full, available selection (single-variant products always have one).
      const n = included.length;
      const needsSelection = included.some((item) => !item.variant || !item.variant.available);
      if (needsSelection) {
        this.addBtn.textContent = this.addBtn.dataset.textSelect;
      } else {
        this.addBtn.textContent = n === 1
          ? this.addBtn.dataset.textOne
          : this.addBtn.dataset.textMany.replace('[count]', n);
      }
      this.addBtn.disabled = n === 0 || needsSelection;
    }

    async addToCart() {
      this.cartErrorEl.hidden = true;
      const included = this.items.filter((item) => item.included);
      const invalid = included.filter((item) => !item.validate());
      if (invalid.length) {
        this.list.scrollTo({ top: invalid[0].el.offsetTop - this.list.offsetTop, behavior: 'smooth' });
        return;
      }

      const sectionsToUpdate = ['page-header', 'cart-drawer'].map((sel) => document.querySelector(sel)).filter((el) => el);
      const body = {
        items: included.map((item) => ({ id: item.variant.id, quantity: 1 })),
        sections: sectionsToUpdate.map((el) => el.dataset.sectionId).join(','),
        sections_url: window.location.pathname
      };

      this.addBtn.disabled = true;
      this.addBtn.classList.add('is-loading');

      try {
        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || response.status);

        if (theme.settings.afterAddToCart === 'page') {
          window.location.href = theme.routes.cart;
          return;
        }

        if (data.sections && sectionsToUpdate.length === Object.keys(data.sections).length) {
          sectionsToUpdate.forEach((el) => el.updateFromCartChange(data.sections[el.dataset.sectionId]));
        } else {
          document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
        }

        included.forEach((item) => {
          this.dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true, detail: { variantId: item.variant.id } }));
        });

        this.drawer.close(() => {
          const cartDrawer = document.querySelector('.js-cart-drawer');
          if (theme.settings.afterAddToCart === 'drawer' && cartDrawer) cartDrawer.open(this.card);
        });
      } catch (error) {
        this.cartErrorEl.textContent = typeof error.message === 'string' && error.message ? error.message : 'Sorry, something went wrong. Please try again.';
        this.cartErrorEl.hidden = false;
        // Some items may have been added before the error.
        document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
      } finally {
        this.addBtn.classList.remove('is-loading');
        this.update();
      }
    }
  }

  customElements.define('gl-complete-the-look', CompleteTheLook);
}
